import { parseMapViewPreferences } from "../helpers/mapPreferences.helpers";
import MapPreferencesService from "../services/MapPreferences";
import { MapPreferencesCache } from "../utils/MapPreferencesCache";
import { MapViewPreferences } from "../types/mapPreferences.types";

export interface MapPreferencesRepository {
  load(userId: string, accountId: string): Promise<MapViewPreferences | null>;
  save(
    userId: string,
    accountId: string,
    preferences: MapViewPreferences
  ): Promise<void>;
}

export class LocalMapPreferencesRepository implements MapPreferencesRepository {
  async load(
    userId: string,
    accountId: string
  ): Promise<MapViewPreferences | null> {
    return MapPreferencesCache.get(userId, accountId);
  }

  async save(
    userId: string,
    accountId: string,
    preferences: MapViewPreferences
  ): Promise<void> {
    MapPreferencesCache.save(userId, accountId, preferences);
  }
}

/**
 * Offline-first repository for when the backend endpoint is available.
 * Enable via REACT_APP_MAP_PREFERENCES_API_ENABLED=true
 */
export class ApiMapPreferencesRepository implements MapPreferencesRepository {
  private localRepository = new LocalMapPreferencesRepository();

  async load(
    userId: string,
    accountId: string
  ): Promise<MapViewPreferences | null> {
    try {
      const response = await MapPreferencesService.getMapPreferences(
        userId,
        accountId
      );

      if (response.status === 200 && response.data) {
        const preferences = parseMapViewPreferences(response.data);

        if (preferences) {
          await this.localRepository.save(userId, accountId, preferences);
          return preferences;
        }
      }
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;

      if (status !== 404) {
        console.warn("Failed to load map preferences from API", error);
      }
    }

    return this.localRepository.load(userId, accountId);
  }

  async save(
    userId: string,
    accountId: string,
    preferences: MapViewPreferences
  ): Promise<void> {
    await this.localRepository.save(userId, accountId, preferences);

    try {
      await MapPreferencesService.saveMapPreferences(
        userId,
        accountId,
        preferences
      );
    } catch (error) {
      console.warn("Failed to save map preferences to API", error);
    }
  }
}

export const createMapPreferencesRepository = (): MapPreferencesRepository => {
  const isApiEnabled =
    process.env.REACT_APP_MAP_PREFERENCES_API_ENABLED === "true";

  return isApiEnabled
    ? new ApiMapPreferencesRepository()
    : new LocalMapPreferencesRepository();
};

export const mapPreferencesRepository = createMapPreferencesRepository();
